import * as React from "react";
import { FC, useEffect, useMemo, useCallback, useState } from "react";
import { connect, Dispatch } from "react-redux";
import { FileWithPreview } from "react-dropzone";
import { format } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import type { IScope } from "reducers/scope";
import Grid from "@material-ui/core/Grid";
import { themeOptions } from "styles/theme";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { MediaDialog } from "components/dialog/media-dialog";
import { IStore } from "reducers";
import { IItemRecord } from "records/item/item";
import FilesIcon from "components/icons/files";
import NotificationError from "components/notification-banners";
import { Preview } from "components/product-media/preview";
import { Uploader } from "components/product-media/components/uploader";
import Reminders from "components/product-media/components/reminders";
import { MEDIA_TYPE_IMAGE } from "constants/media";
import { IAsset } from "constants/assets";
import { ContentLoading } from "components/content-loading";
import { IMediaAsset, IMediaAssetMap } from "constants/media-asset";
import {
    fetchMediaAssets,
    mutateMediaAssets,
    resetProductMediaAssets,
    resetProductMedia,
    uploadImage,
} from "actions/product-media-actions";
import { createNewMediaKey, transformMediaMap } from "helpers/manage-media";
import { DATATEST } from "constants/integration";

const MAX_SIZE = 5; // MB
const MIN_HEIGHT = 500; // px
const MIN_WIDTH = 500; // px

// prettier-ignore
interface IProps {
    product: IItemRecord;
    open: boolean;
    onClose (reload?: boolean): void;
}

interface IReduxProps {
    asset: IAsset;
    error: string | Error;
    isUploading: boolean;
    mediaAssetMap: IMediaAssetMap;
    revision: string;
    isUpdating: boolean;
    isFetching: boolean;
    scope: IScope;
}

// prettier-ignore
interface IDispatchProps {
    resetProductMedia(): void;
    fetchProductMedia(id: string): void;
    updateMedia(productId: string, revision: string, mediaMap: IMediaAssetMap): void;
    onUpload (product: string, id: string): void;
    onReset (): void;
}

const UploadImageDialog: FC<IProps & IReduxProps & IDispatchProps> = (props) => {
    const { product, open, onClose, scope } = props;
    const { asset, error, onReset, mediaAssetMap, revision } = props;
    const { onUpload, isUploading, isUpdating, isFetching, fetchProductMedia, resetProductMedia, updateMedia } = props;

    const mediaMap = useMemo(() => transformMediaMap(mediaAssetMap), [mediaAssetMap]);

    const [image, setImage] = useState<{ id: string; url: string }>(null);
    const [directUploading, setDirectUploading] = useState<boolean>(false); // internal flag of uploader processing
    const [uploadError, setUploadError] = useState<string>(null);
    const [close, setClose] = useState<boolean>(false); // close the dialog right after update is finished

    const errorMessage = useMemo(
        () =>
            format("There were issues with the image(s) you uploaded. Make sure they are the correct file type/size."),
        [],
    );

    useEffect(() => {
        fetchProductMedia(product._id);
    }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => setUploadError(error && errorMessage), [error, errorMessage]);

    const handleClose = useCallback(
        (reload?: boolean) => {
            onReset(); // reset uploading state
            resetProductMedia();
            setUploadError(null);
            onClose(reload);
        },
        [onClose, onReset, resetProductMedia],
    );

    const handleUpload = useCallback(
        (result: { id: string }, file: FileWithPreview) => {
            if (result) {
                if (file) {
                    setImage({ id: result.id, url: file.preview });
                }
                onUpload(product._id, result.id);
            }
        },
        [product, onUpload],
    );

    const handleSave = useCallback(() => {
        if (asset) {
            const assetKey = createNewMediaKey(mediaMap);
            updateMedia(product._id, revision, {
                ...mediaMap,
                [assetKey]: { ...asset, type: MEDIA_TYPE_IMAGE } as IMediaAsset,
            });
            setClose(true);
        } else {
            handleClose();
        }
    }, [product, asset, revision, mediaMap, updateMedia, handleClose]);

    const handleDropImageUpload = useCallback(() => setImage(null), []);

    const handleCloseDialog = useCallback(() => handleClose(false), [handleClose]);

    const handleReject = useCallback(() => setUploadError(errorMessage), [errorMessage]);
    const handleUploadStart = useCallback(() => setUploadError(null), []);
    const handleHideError = useCallback(() => setUploadError(null), []);
    const handleError = useCallback(
        ({ message }: { message: string } | Error) => setUploadError(message || errorMessage),
        [errorMessage],
    );

    useEffect(() => {
        if (close && !isUpdating) {
            if (!error) {
                handleClose(true);
            } else {
                setClose(false);
            }
        }
    }, [close, isUpdating, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

    const classes = useStyles({});
    return (
        <MediaDialog
            open={open}
            title={format("Add Images")}
            onClose={handleCloseDialog}
            onSave={handleSave}
            disabled={directUploading || isUploading || isUpdating || isFetching}
        >
            <div className={classes.contentTitle}>{format("Review the image specifications IMAGE")}</div>
            <NotificationError classes={{ root: classes.error }} message={uploadError} onClose={handleHideError} />
            <Grid container={true} wrap="wrap" justify="space-around" spacing={2}>
                <Grid className={classes.margin} item={true}>
                    {/*prettier-ignore*/}
                    <Uploader
                        inputProps={{
                            "data-test": DATATEST.MEDIA_DIALOG.IMAGE_UPLOADER,
                        }}
                        accept="image/jpeg,image/jpg,image/png"
                        maxSize={MAX_SIZE * 1024 * 1024}
                        loading={directUploading || isUploading}
                        onUpload={handleUpload}
                        onUploadError={handleError}
                        onDropReject={handleReject}
                        onUploadStart={handleUploadStart}
                        onLoading={setDirectUploading}
                        scope={scope}
                        minWidth={MIN_WIDTH}
                        minHeight={MIN_HEIGHT}
                    >
                        <div className={classes.dragContainer}>
                            <FilesIcon className={`${classes.icon} ${classes.iconFiles}`}/>
                            <div className={classes.dragLabel}>
                                <FormatMessage
                                    path="Drag your image files here |br| or |span|click here|span| to select"
                                    elements={{ br: <br/>, span: <span className={classes.click}/> }}
                                />
                            </div>
                        </div>
                    </Uploader>
                </Grid>
                <Grid
                    className={classes.margin}
                    data-test={DATATEST.MEDIA_DIALOG.IMAGE_SPECIFICATIONS}
                    item={true}
                    xs={true}
                >
                    <Reminders title={format("Image specifications:")}>
                        <li>{format("JPG_PNG only")}</li>
                        <li>
                            {format("Image size: |recommended| recommended, min width |w|, min height |h|", {
                                w: `${MIN_WIDTH}px`,
                                h: `${MIN_HEIGHT}px`,
                                recommended: "1400px x 2200px",
                            })}
                        </li>
                        <li>
                            {format("File size: |min| - |recommended| recommended, |max| max", {
                                min: "500 KB",
                                max: `${MAX_SIZE} MB`,
                                recommended: "1.5 MB",
                            })}
                        </li>
                    </Reminders>
                </Grid>
            </Grid>
            {image && (
                <div>
                    <Preview className={classes.imagePreview} onDelete={handleDropImageUpload}>
                        <img src={image.url} alt={"Widget Image"} />
                    </Preview>
                </div>
            )}
            {isUpdating && <ContentLoading className={classes.loading} />}
        </MediaDialog>
    );
};

const useStyles = makeStyles({
    contentTitle: {
        fontSize: 14,
        padding: 10,
    },
    error: {
        margin: 10,
    },
    loading: {
        left: -24,
    },
    click: {
        textDecoration: "underline",
    },
    icon: {
        width: 160,
        height: 160,
        padding: 30,
        margin: 10,
    },
    iconFiles: {
        color: themeOptions.palette.grey,
    },
    dragLabel: {
        fontSize: 14,
        textAlign: "center",
        lineHeight: "1.8em",
        color: themeOptions.palette.darkGrey,
    },
    margin: {
        margin: 20,
        ["@media(max-width: 600px)"]: {
            margin: 10,
        },
    },
    dragContainer: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    imagePreview: {
        display: "flex",
        justifyContent: "center",
        "& img": {
            width: "100%",
            alignSelf: "flex-start",
        },
    },
});

const mapStateToProps = (store: IStore): IReduxProps => ({
    asset: store.productMedia.payload,
    error: store.productMedia.error,
    isUploading: store.productMedia.isUploading,
    mediaAssetMap: store.productMediaAssets?.payload?.media as IMediaAssetMap,
    isFetching: store.productMediaAssets?.isFetching,
    isUpdating: store.productMediaAssets?.isUpdating,
    revision: store.productMediaAssets?.payload?.revision,
    scope: store.scope,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    fetchProductMedia: (productId: string) => dispatch(fetchMediaAssets(productId)),
    resetProductMedia: () => dispatch(resetProductMediaAssets()),
    updateMedia: (productId: string, revision: string, mediaMap: IMediaAssetMap) =>
        dispatch(mutateMediaAssets(productId, revision, mediaMap)),
    onUpload: (product: string, id: string) => dispatch(uploadImage(product, id)),
    onReset: () => dispatch(resetProductMedia()),
});

export default connect<IReduxProps, IDispatchProps, IProps>(mapStateToProps, mapDispatchToProps)(UploadImageDialog);
